import { API_CONFIG } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { apiResponseSchema } from '../lib/schemas';
import { logger } from '../utils/logger';

export interface DateUpdate {
  date: string;
  status: 'absence' | 'vacation' | 'bridge' | 'remove';
}

export interface BatchUpdateRequest {
  updates: DateUpdate[];
}

export interface BatchUpdateResponse {
  has_existing_hours: boolean;
  dates_with_hours: string[];
  result: {
    created: number;
    removed: number;
    errors?: string[];
  };
}

export interface CheckExistingHoursRequest {
  dates: string[];
}

export interface CheckExistingHoursResponse {
  has_existing_hours: boolean;
  dates_with_hours: string[];
}

export interface AbsenceVacationEntry {
  id: string;
  employee_id: string;
  project_id: string;
  task_id: string;
  date_of_year: string;
  hours_worked: number;
  calendar_week: string;
  status: string;
}

const absenceVacationService = {
  async markAbsenceVacation(
    employeeId: string,
    dates: string[],
    status: 'absence' | 'vacation'
  ): Promise<{ hasExistingHours: boolean; datesWithHours: string[] }> {
    try {
      const updates: DateUpdate[] = dates.map((date) => ({
        date,
        status,
      }));

      const response = await apiClient.post<BatchUpdateResponse>(
        `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${employeeId}/absence-vacation/batch`,
        { updates }
      );

      return {
        hasExistingHours: response.data.has_existing_hours,
        datesWithHours: response.data.dates_with_hours || [],
      };
    } catch (error) {
      logger.error('Failed to mark absence/vacation', error);
      throw error;
    }
  },

  async removeAbsenceVacation(
    employeeId: string,
    dates: string[]
  ): Promise<void> {
    try {
      const updates: DateUpdate[] = dates.map((date) => ({
        date,
        status: 'remove',
      }));

      await apiClient.post<BatchUpdateResponse>(
        `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${employeeId}/absence-vacation/batch`,
        { updates }
      );
    } catch (error) {
      logger.error('Failed to remove absence/vacation', error);
      throw error;
    }
  },

  async processBatchUpdates(
    employeeId: string,
    updates: DateUpdate[]
  ): Promise<BatchUpdateResponse> {
    try {
      const response = await apiClient.post<BatchUpdateResponse>(
        `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${employeeId}/absence-vacation/batch`,
        { updates }
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to process batch updates', error);
      throw error;
    }
  },

  async checkExistingHours(
    employeeId: string,
    dates: string[]
  ): Promise<CheckExistingHoursResponse> {
    try {
      const response = await apiClient.post<CheckExistingHoursResponse>(
        `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${employeeId}/absence-vacation/check`,
        { dates }
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to check existing hours', error);
      throw error;
    }
  },

  async getAbsenceVacation(
    employeeId: string,
    startDate: string,
    endDate: string
  ): Promise<AbsenceVacationEntry[]> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        message: string;
        data: AbsenceVacationEntry[];
      }>(
        `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${employeeId}/absence-vacation?start_date=${startDate}&end_date=${endDate}`
      );

      return response.data.data || [];
    } catch (error) {
      logger.error('Failed to get absence/vacation entries', error);
      throw error;
    }
  },
};

export default absenceVacationService;
